import { useCallback, useMemo, useState } from "react";

import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { useCurrentCampaignTags } from "app/pages/digital-marketing/keywords-campaigns/reports/page-filters/tags/hooks/useCurrentCampaignTags";

export const useTagsState = (campaignConfig: IRTCurrentCampaignData) => {
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState("");
    const [isComboboxOpen, setIsComboboxOpen] = useState(false);

    const allExistingTags = useCurrentCampaignTags(campaignConfig);

    const visibleTagOptions = useMemo(() => {
        const filtered = allExistingTags.filter((tag) => !selectedTags.includes(tag));
        const lowerSearch = searchValue.toLowerCase().trim();
        if (!lowerSearch) {
            return filtered;
        }
        return filtered.filter((tag) => tag.toLowerCase().startsWith(lowerSearch));
    }, [allExistingTags, selectedTags, searchValue]);

    const showCreateOption = useMemo(() => {
        const trimmed = searchValue.trim();
        if (!trimmed) {
            return false;
        }
        const lowerSearch = trimmed.toLowerCase();
        const exactMatchExists = allExistingTags.some((tag) => tag.toLowerCase() === lowerSearch);
        const alreadySelected = selectedTags.some((tag) => tag.toLowerCase() === lowerSearch);
        return !exactMatchExists && !alreadySelected;
    }, [allExistingTags, selectedTags, searchValue]);

    const handleAddTag = useCallback((tag: string) => {
        setSelectedTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
        setSearchValue("");
        setIsComboboxOpen(false);
    }, []);

    const handleRemoveTag = useCallback((tag: string) => {
        setSelectedTags((prev) => prev.filter((t) => t !== tag));
    }, []);

    return {
        selectedTags,
        isComboboxOpen,
        setIsComboboxOpen,
        searchValue,
        setSearchValue,
        visibleTagOptions,
        showCreateOption,
        handleAddTag,
        handleRemoveTag,
    };
};
